import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useRealEstate, PROPERTY_STATUS } from '../../contexts/RealEstateContext';

const getStatusColor = (status) => {
  switch (status) {
    case PROPERTY_STATUS.ACTIVE:
      return 'success';
    case PROPERTY_STATUS.CLOSING:
      return 'secondary';
    case PROPERTY_STATUS.ON_CONTRACT:
      return 'warning';
    default:
      return 'default';
  }
};

const PropertyStatusReport = () => {
  const { getAllProperties } = useRealEstate();
  const properties = getAllProperties();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Get unique agents
  const agents = useMemo(() => {
    const agentSet = new Set(properties.map((p) => p.listingAgentName));
    return Array.from(agentSet);
  }, [properties]);

  // Filter properties
  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      const matchesSearch =
        property.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.listingAgentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (property.buyerName && property.buyerName.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || property.status === statusFilter;
      const matchesAgent = agentFilter === 'all' || property.listingAgentName === agentFilter;

      return matchesSearch && matchesStatus && matchesAgent;
    });
  }, [properties, searchTerm, statusFilter, agentFilter]);

  // Calculate progress for closing properties
  const getClosingProgress = (status) => {
    switch (status) {
      case PROPERTY_STATUS.ACTIVE:
        return 25;
      case PROPERTY_STATUS.ON_CONTRACT:
        return 60;
      case PROPERTY_STATUS.CLOSING:
        return 90;
      default:
        return 0;
    }
  };

  return (
    <Box>
      {/* Search and Filters */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search properties, agents, buyers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              border: '1px solid',
              borderColor: showFilters ? 'primary.main' : 'grey.300',
              color: showFilters ? 'primary.main' : 'text.secondary',
              borderRadius: 1,
            }}
          >
            <FilterListIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value={PROPERTY_STATUS.ACTIVE}>Active</MenuItem>
                <MenuItem value={PROPERTY_STATUS.ON_CONTRACT}>On Contract</MenuItem>
                <MenuItem value={PROPERTY_STATUS.CLOSING}>Closing</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Listing Agent</InputLabel>
              <Select
                value={agentFilter}
                label="Listing Agent"
                onChange={(e) => setAgentFilter(e.target.value)}
              >
                <MenuItem value="all">All Agents</MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent} value={agent}>
                    {agent}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>
      </Box>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredProperties.length} of {properties.length} properties
      </Typography>

      {/* Property Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredProperties.map((property) => (
          <Card
            key={property.id}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderColor: 'grey.300',
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HomeIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                      {property.address}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="text.primary">
                      ${property.price.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={property.status}
                  color={getStatusColor(property.status)}
                  size="medium"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {/* Property Details */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BadgeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Listing Agent
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {property.listingAgentName}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Buyer
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {property.buyerName || 'No buyer yet'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Last Updated
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {property.lastUpdated}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Property Info */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`${property.bedrooms} Bed`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
                <Chip
                  label={`${property.bathrooms} Bath`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
                <Chip
                  label={`${property.sqft.toLocaleString()} sqft`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>

              {/* Status Details */}
              <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Status Details
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {property.statusDetails}
                </Typography>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Transaction Progress
                  </Typography>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    {getClosingProgress(property.status)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getClosingProgress(property.status)}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2,
                      backgroundColor: 'grey.500',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        ))}

        {filteredProperties.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              No properties found matching your criteria.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PropertyStatusReport;
